import React, { useState } from 'react';
import { Control, Controller, FieldErrors, UseFormRegister, UseFormWatch } from 'react-hook-form';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { DollarSign, Package } from 'lucide-react';
import type { ProductFormValues } from '@/types/product-type';

interface Props {
  control: Control<ProductFormValues | any>;
  register: UseFormRegister<ProductFormValues | any>;
  errors: FieldErrors<ProductFormValues | any>;
  watch: UseFormWatch<ProductFormValues | any>;
}

export function ProductPricingWizardSection({ control, register, errors, watch }: Props) {
  const [priceInput, setPriceInput] = useState('');
  const [costInput, setCostInput] = useState('');

  const price = watch('price') ?? 0;
  const cost  = watch('cost')  ?? 0;
  const selectedColors = (watch('colorValues') ?? []) as string[];
  const selectedSizes  = (watch('sizeValues')  ?? []) as string[];
  
  const c = Math.max(selectedColors.length, 1);
  const s = Math.max(selectedSizes.length, 1);
  const variantCount = (selectedColors.length > 0 || selectedSizes.length > 0) ? c * s : 0;

  const profit       = Math.max(0, Number(price) - Number(cost));
  const margin       = Number(price) > 0 ? (profit / Number(price)) * 100 : 0;
  const initialStock = Number(watch('initStock')) || 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Pricing */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <DollarSign size={18} color="#2563eb" />
          <Typography variant="subtitle1" fontWeight={600}>Pricing</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Price (Amount to sell to customer in USD)"
                  placeholder="0"
                  InputLabelProps={{ shrink: true }}
                  required
                  value={priceInput !== '' ? priceInput : (field.value === 0 || field.value === undefined ? '' : field.value.toString())}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPriceInput(v);
                    if (v === '') { field.onChange(0); return; }
                    if (/^\d*\.?\d*$/.test(v)) {
                      const n = parseFloat(v);
                      if (!isNaN(n)) field.onChange(n);
                    }
                  }}
                  onBlur={(e) => {
                    const v = e.target.value;
                    setPriceInput('');
                    field.onChange(v === '' || v === '.' ? 0 : Math.round(parseFloat(v) * 100) / 100);
                    field.onBlur();
                  }}
                  onFocus={() => setPriceInput(field.value === 0 ? '' : field.value?.toString() ?? '')}
                  error={!!errors.price}
                  helperText={errors.price?.message as string}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="cost"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label="Cost (Original amount to buy from supplier in USD)"
                  placeholder="0"
                  InputLabelProps={{ shrink: true }}
                  value={costInput !== '' ? costInput : (field.value === 0 || field.value === undefined ? '' : field.value.toString())}
                  onChange={(e) => {
                    const v = e.target.value;
                    setCostInput(v);
                    if (v === '') { field.onChange(0); return; }
                    if (/^\d*\.?\d*$/.test(v)) {
                      const n = parseFloat(v);
                      if (!isNaN(n)) field.onChange(n);
                    }
                  }}
                  onBlur={(e) => {
                    const v = e.target.value;
                    setCostInput('');
                    field.onChange(v === '' || v === '.' ? 0 : Math.round(parseFloat(v) * 100) / 100);
                    field.onBlur();
                  }}
                  onFocus={() => setCostInput(field.value === 0 ? '' : field.value?.toString() ?? '')}
                  error={!!errors.cost}
                  helperText={errors.cost?.message as string}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Profit display */}
        {(Number(price) > 0 || Number(cost) > 0) && (
          <Box
            sx={{
              mt: 2, p: 2, borderRadius: 2,
              bgcolor: profit >= 0 ? '#f0fdf4' : '#fef2f2',
              border: '1px solid', borderColor: profit >= 0 ? '#bbf7d0' : '#fecaca',
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">Profit per unit</Typography>
              <Typography variant="subtitle1" fontWeight={700} color={profit >= 0 ? '#16a34a' : '#dc2626'}>
                ${profit.toFixed(2)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Profit margin</Typography>
              <Typography variant="subtitle1" fontWeight={700} color={profit >= 0 ? '#16a34a' : '#dc2626'}>
                {margin.toFixed(1)}%
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Inventory */}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Package size={18} color="#2563eb" />
          <Typography variant="subtitle1" fontWeight={600}>Inventory</Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Unit"
              placeholder="e.g. piece, kg"
              InputLabelProps={{ shrink: true }}
              {...register('unit')}
              error={!!errors.unit}
              helperText={errors.unit?.message as string}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Unit of measurement for this product
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Initial Stock"
              placeholder="0"
              type="number"
              required
              InputLabelProps={{ shrink: true }}
              {...register('initStock', { valueAsNumber: true })}
              error={!!errors.initStock}
              helperText={errors.initStock?.message as string}
              inputProps={{ min: 0 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Starting inventory quantity
            </Typography>
          </Grid>
        </Grid>

        {/* Stock distribution note */}
        {initialStock > 0 && variantCount > 1 && (
          <Box
            sx={{
              mt: 2, p: 2, borderRadius: 2,
              bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Package size={16} color="#2563eb" />
              <Typography variant="body2" color="#2563eb" fontWeight={600}>
                Stock Distribution
              </Typography>
            </Box>
            <Typography variant="body2" color="#1e40af" mt={0.5}>
              The initial stock of <strong>{initialStock}</strong> units will be evenly distributed across all product variants
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
